import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, Container, Divider, GlobalStyles, IconButton, Paper, Stack, Typography } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import ScrollToTopButton from '../../components/ScrollToTopButton';

const sectionSx = {
  maxWidth: 1240,
  py: { xs: 4, md: 6 },
  px: { xs: 1.5, sm: 2.5, md: 3 },
};

export default function LegalPage({ title, updatedAt, summary, toc = [], children }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktopLegalLayout = useMediaQuery(theme.breakpoints.up('lg'));
  const [activeSectionId, setActiveSectionId] = useState(toc[0]?.id || '');
  const mobileTocRef = useRef(null);
  const desktopTocWrapperRef = useRef(null);
  const desktopTocCardRef = useRef(null);
  const footerRef = useRef(null);
  const [canScrollTocLeft, setCanScrollTocLeft] = useState(false);
  const [canScrollTocRight, setCanScrollTocRight] = useState(false);
  const [desktopTocStyle, setDesktopTocStyle] = useState({
    mode: 'static',
    top: 96,
    width: null,
    left: null,
    maxHeight: null,
    absoluteTop: 0,
  });

  const canGoBack = (() => {
    try {
      const idx = window.history?.state?.idx;
      if (typeof idx === 'number') return idx > 0;
      return window.history.length > 1;
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    if (!toc.length) {
      setActiveSectionId('');
      return undefined;
    }

    let ticking = false;

    const updateActiveSection = () => {
      const headings = toc
        .map((item) => ({ ...item, element: document.getElementById(item.id) }))
        .filter((item) => item.element);

      if (!headings.length) {
        ticking = false;
        return;
      }

      let currentId = headings[0].id;

      headings.forEach((item) => {
        const top = item.element.getBoundingClientRect().top;
        if (top <= 180) {
          currentId = item.id;
        }
      });

      const nearBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 120;

      if (nearBottom) {
        currentId = headings[headings.length - 1].id;
      }

      setActiveSectionId((previous) => (previous === currentId ? previous : currentId));
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateActiveSection);
        ticking = true;
      }
    };

    updateActiveSection();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [toc]);

  useEffect(() => {
    const container = mobileTocRef.current;

    if (!container || !toc.length) {
      setCanScrollTocLeft(false);
      setCanScrollTocRight(false);
      return undefined;
    }

    const updateTocScrollState = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const maxScrollLeft = Math.max(scrollWidth - clientWidth, 0);

      setCanScrollTocLeft(scrollLeft > 8);
      setCanScrollTocRight(scrollLeft < maxScrollLeft - 8);
    };

    updateTocScrollState();
    container.addEventListener('scroll', updateTocScrollState, { passive: true });
    window.addEventListener('resize', updateTocScrollState);

    return () => {
      container.removeEventListener('scroll', updateTocScrollState);
      window.removeEventListener('resize', updateTocScrollState);
    };
  }, [toc]);

  useEffect(() => {
    if (!toc.length || !isDesktopLegalLayout) {
      setDesktopTocStyle({
        mode: 'static',
        top: 96,
        width: null,
        left: null,
        maxHeight: null,
        absoluteTop: 0,
      });
      return undefined;
    }

    let ticking = false;

    const updateDesktopTocPosition = () => {
      const wrapper = desktopTocWrapperRef.current;
      const card = desktopTocCardRef.current;
      const footer = footerRef.current;

      if (!wrapper || !card || !footer) {
        ticking = false;
        return;
      }

      const topOffset = 96;
      const bottomGap = 24;
      const minimumHeight = 240;
      const wrapperRect = wrapper.getBoundingClientRect();
      const footerRect = footer.getBoundingClientRect();
      const wrapperTop = wrapperRect.top + window.scrollY;
      const footerTop = footerRect.top + window.scrollY;
      const viewportHeight = window.innerHeight;
      const maxHeight = Math.max(viewportHeight - topOffset - bottomGap, minimumHeight);
      const cardHeight = Math.min(card.scrollHeight, maxHeight);
      const shouldFix = window.scrollY + topOffset >= wrapperTop;
      const shouldRelease = window.scrollY + topOffset + cardHeight + bottomGap >= footerTop;

      const nextStyle = {
        mode: shouldFix ? (shouldRelease ? 'absolute' : 'fixed') : 'static',
        top: topOffset,
        width: wrapperRect.width,
        left: wrapperRect.left,
        maxHeight,
        absoluteTop: Math.max(0, footerTop - wrapperTop - cardHeight - bottomGap),
      };

      setDesktopTocStyle((previous) => {
        if (
          previous.mode === nextStyle.mode &&
          previous.top === nextStyle.top &&
          previous.width === nextStyle.width &&
          previous.left === nextStyle.left &&
          previous.maxHeight === nextStyle.maxHeight &&
          previous.absoluteTop === nextStyle.absoluteTop
        ) {
          return previous;
        }

        return nextStyle;
      });

      ticking = false;
    };

    const handlePositioning = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateDesktopTocPosition);
        ticking = true;
      }
    };

    handlePositioning();
    window.addEventListener('scroll', handlePositioning, { passive: true });
    window.addEventListener('resize', handlePositioning);

    return () => {
      window.removeEventListener('scroll', handlePositioning);
      window.removeEventListener('resize', handlePositioning);
    };
  }, [isDesktopLegalLayout, toc]);

  const scrollMobileToc = (direction) => {
    const container = mobileTocRef.current;

    if (!container) return;

    const scrollAmount = Math.max(container.clientWidth * 0.72, 180);
    container.scrollBy({
      left: direction * scrollAmount,
      behavior: 'smooth',
    });
  };

  const handleTocNavigation = (event, id) => {
    event.preventDefault();

    const target = document.getElementById(id);

    if (!target) return;

    target.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

    if (window.location.hash !== `#${id}`) {
      window.history.replaceState(null, '', `#${id}`);
    }
  };

  return (
    <Box
      className="legal-page-root"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
        background:
          'radial-gradient(circle at top left, rgba(1,111,87,0.08), transparent 28%), linear-gradient(180deg, #f7faf9 0%, #ffffff 22%)',
      }}
    >
      <GlobalStyles
        styles={{
          html: {
            scrollBehavior: 'smooth',
          },
          '*, *::before, *::after': {
            boxSizing: 'border-box',
          },
          '.legal-page-root img, .legal-page-root svg, .legal-page-root video': {
            maxWidth: '100%',
          },
          '.legal-page-root a, .legal-page-root p, .legal-page-root li, .legal-page-root td, .legal-page-root th, .legal-page-root code': {
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          },
          '@media print': {
            html: {
              scrollBehavior: 'auto',
            },
            body: {
              background: '#ffffff !important',
            },
            '.legal-print-hide': {
              display: 'none !important',
            },
            '.legal-page-root': {
              background: '#ffffff !important',
            },
            '.legal-hero-card, .legal-content-card, .legal-toc-card': {
              boxShadow: 'none !important',
              border: '1px solid #d9dee8 !important',
              background: '#ffffff !important',
            },
            '.legal-layout-grid': {
              display: 'block !important',
            },
            '.legal-content-card': {
              padding: '0 !important',
              border: 'none !important',
            },
            '.legal-page-container': {
              maxWidth: '100% !important',
              padding: '0 !important',
            },
            '.legal-page-main': {
              padding: '0 !important',
            },
            'a[href]:after': {
              content: '""',
              display: 'none !important',
            },
          },
        }}
      />
      <Box className="legal-print-hide">
        <Navbar />
      </Box>
      <Box component="main" className="legal-page-main" sx={{ flex: 1 }}>
        <Container className="legal-page-container" sx={sectionSx}>
          <Paper
            elevation={0}
            className="legal-hero-card"
            sx={{
              p: { xs: 2, sm: 2.5, md: 4 },
              borderRadius: { xs: 3, md: 4 },
              border: '1px solid',
              borderColor: 'rgba(13, 28, 79, 0.08)',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.96))',
              boxShadow: '0 24px 64px rgba(13, 28, 79, 0.08)',
              mb: { xs: 3, md: 4 },
            }}
          >
            <Button
              type="button"
              onClick={() => (canGoBack ? navigate(-1) : navigate('/', { replace: true }))}
              startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 18 }} />}
              className="legal-print-hide"
              sx={{
                mb: 2.5,
                px: { xs: 1.25, sm: 1.6 },
                py: 0.85,
                minWidth: 'auto',
                maxWidth: '100%',
                textTransform: 'none',
                fontWeight: 800,
                letterSpacing: '0.01em',
                alignSelf: 'flex-start',
                fontSize: { xs: '0.9rem', sm: '0.95rem' },
                color: '#0D1C4F',
                borderRadius: 999,
                border: '1px solid rgba(13, 28, 79, 0.1)',
                backgroundColor: 'rgba(255,255,255,0.9)',
                boxShadow: '0 10px 24px rgba(13, 28, 79, 0.06)',
                backdropFilter: 'blur(8px)',
                '&:hover': {
                  backgroundColor: 'rgba(1, 111, 87, 0.08)',
                  borderColor: 'rgba(1, 111, 87, 0.22)',
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
              }}
              variant="outlined"
              color="inherit"
            >
              {isSmallScreen ? 'Back' : 'Back to previous page'}
            </Button>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 2, md: 2.5 }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              <Box sx={{ maxWidth: { xs: '100%', md: 760 }, minWidth: 0 }}>
                <Chip
                  label="Legal"
                  sx={{
                    mb: 1.5,
                    fontWeight: 700,
                    bgcolor: 'rgba(1, 111, 87, 0.1)',
                    color: '#016f57',
                    maxWidth: '100%',
                  }}
                />
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 900,
                    letterSpacing: '-0.03em',
                    mb: 1.25,
                    lineHeight: { xs: 1.15, md: 1.1 },
                    fontSize: { xs: '1.7rem', sm: '2rem', md: '2.75rem' },
                  }}
                >
                  {title}
                </Typography>
                {summary && (
                  <Typography sx={{ color: 'text.secondary', lineHeight: 1.72, fontSize: { xs: '0.95rem', sm: '1rem', md: '1.08rem' }, maxWidth: 700 }}>
                    {summary}
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { md: 220 },
                  maxWidth: { xs: '100%', md: 260 },
                  p: { xs: 1.75, sm: 2 },
                  borderRadius: 3,
                  bgcolor: 'rgba(13, 28, 79, 0.03)',
                  border: '1px solid rgba(13, 28, 79, 0.08)',
                }}
              >
                <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.08em' }}>
                  Document Status
                </Typography>
                <Typography sx={{ fontWeight: 800, mt: 0.5 }}>Official Policy</Typography>
                {updatedAt && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    Last updated: {updatedAt}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Paper>

          <Box
            className="legal-layout-grid"
            sx={{
              display: { xs: 'block', lg: 'flex' },
              gap: { xs: 2.25, sm: 3, lg: 4 },
              alignItems: 'start',
            }}
          >
            <Paper
              elevation={0}
              className="legal-content-card"
              sx={{
                p: { xs: 1.5, sm: 2.25, md: 4 },
                borderRadius: { xs: 3, md: 4 },
                border: '1px solid',
                borderColor: 'rgba(13, 28, 79, 0.08)',
                backgroundColor: 'rgba(255,255,255,0.98)',
                boxShadow: '0 18px 48px rgba(13, 28, 79, 0.06)',
                overflow: 'hidden',
                flex: '1 1 auto',
                minWidth: 0,
              }}
            >
              {toc.length > 0 && (
                <Box sx={{ display: { xs: 'block', lg: 'none' }, mb: 3 }} className="legal-print-hide">
                  <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.08em' }}>
                    On This Page
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25 }}>
                    <IconButton
                      aria-label="Scroll sections left"
                      onClick={() => scrollMobileToc(-1)}
                      disabled={!canScrollTocLeft}
                      sx={{
                        flex: '0 0 auto',
                        width: 36,
                        height: 36,
                        border: '1px solid rgba(13, 28, 79, 0.12)',
                        bgcolor: '#ffffff',
                        boxShadow: '0 10px 24px rgba(13, 28, 79, 0.06)',
                        '&.Mui-disabled': {
                          opacity: 0.42,
                          bgcolor: 'rgba(13, 28, 79, 0.03)',
                        },
                      }}
                    >
                      <ChevronLeftRoundedIcon />
                    </IconButton>
                    <Box
                      ref={mobileTocRef}
                      sx={{
                        display: 'flex',
                        gap: 1,
                        minWidth: 0,
                        flex: 1,
                        overflowX: 'auto',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        scrollSnapType: 'x proximity',
                        scrollBehavior: 'smooth',
                        '&::-webkit-scrollbar': {
                          display: 'none',
                        },
                      }}
                    >
                      {toc.filter((item) => item.level === 2).map((item) => {
                        const isActive = item.id === activeSectionId;

                        return (
                          <Chip
                            key={item.id}
                            label={item.text}
                            component="a"
                            href={`#${item.id}`}
                            clickable
                            onClick={(event) => handleTocNavigation(event, item.id)}
                            sx={{
                              textDecoration: 'none',
                              bgcolor: isActive ? '#016f57' : 'rgba(1, 111, 87, 0.08)',
                              color: isActive ? '#ffffff' : '#016f57',
                              fontWeight: 700,
                              transition: 'all 0.18s ease',
                              flex: '0 0 auto',
                              whiteSpace: 'nowrap',
                              scrollSnapAlign: 'start',
                              maxWidth: '70vw',
                            }}
                          />
                        );
                      })}
                    </Box>
                    <IconButton
                      aria-label="Scroll sections right"
                      onClick={() => scrollMobileToc(1)}
                      disabled={!canScrollTocRight}
                      sx={{
                        flex: '0 0 auto',
                        width: 36,
                        height: 36,
                        border: '1px solid rgba(13, 28, 79, 0.12)',
                        bgcolor: '#ffffff',
                        boxShadow: '0 10px 24px rgba(13, 28, 79, 0.06)',
                        '&.Mui-disabled': {
                          opacity: 0.42,
                          bgcolor: 'rgba(13, 28, 79, 0.03)',
                        },
                      }}
                    >
                      <ChevronRightRoundedIcon />
                    </IconButton>
                  </Stack>
                </Box>
              )}

              <Box sx={{ lineHeight: { xs: 1.75, md: 1.8 }, color: 'text.primary', minWidth: 0 }}>
                {children}
              </Box>

              <Divider sx={{ mt: 5, mb: 2.5 }} />
              <Typography variant="body2" color="text.secondary">
                Questions? Contact us at{' '}
                <Box component="a" href="mailto:hello@peercampus.in" sx={{ color: '#016f57', fontWeight: 700 }}>
                  hello@peercampus.in
                </Box>
                .
              </Typography>
            </Paper>

            {toc.length > 0 && (
              <Box
                ref={desktopTocWrapperRef}
                className="legal-print-hide"
                sx={{
                  display: { xs: 'none', lg: 'block' },
                  flex: '0 0 296px',
                  width: 296,
                  minWidth: 296,
                  alignSelf: 'flex-start',
                  position: 'relative',
                }}
              >
                <Paper
                  ref={desktopTocCardRef}
                  elevation={0}
                  className="legal-toc-card"
                  sx={{
                    position:
                      desktopTocStyle.mode === 'fixed'
                        ? 'fixed'
                        : desktopTocStyle.mode === 'absolute'
                          ? 'absolute'
                          : 'relative',
                    top: desktopTocStyle.mode === 'fixed' ? desktopTocStyle.top : desktopTocStyle.mode === 'absolute' ? desktopTocStyle.absoluteTop : 0,
                    left: desktopTocStyle.mode === 'fixed' ? desktopTocStyle.left : 'auto',
                    width: desktopTocStyle.mode === 'fixed' ? desktopTocStyle.width : '100%',
                    maxHeight: desktopTocStyle.maxHeight || 'calc(100vh - 120px)',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 2.5,
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'rgba(13, 28, 79, 0.08)',
                    backgroundColor: 'rgba(255,255,255,0.92)',
                    boxShadow: '0 18px 48px rgba(13, 28, 79, 0.06)',
                    overflow: 'hidden',
                  }}
                >
                  <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.08em', flex: '0 0 auto' }}>
                    On This Page
                  </Typography>
                  <Box
                    sx={{
                      mt: 1.5,
                      minHeight: 0,
                      overflowY: 'auto',
                      pr: 0.75,
                      mr: -0.75,
                      scrollbarWidth: 'thin',
                      scrollbarColor: 'rgba(13, 28, 79, 0.22) transparent',
                      '&::-webkit-scrollbar': {
                        width: 8,
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(13, 28, 79, 0.18)',
                        borderRadius: 999,
                      },
                    }}
                  >
                    <Stack spacing={1.2}>
                      {toc.map((item) => (
                        (() => {
                          const isActive = item.id === activeSectionId;

                          return (
                            <Box
                              key={item.id}
                              component="a"
                              href={`#${item.id}`}
                              onClick={(event) => handleTocNavigation(event, item.id)}
                              sx={{
                                display: 'block',
                                color: isActive ? '#016f57' : item.level === 2 ? 'text.primary' : 'text.secondary',
                                textDecoration: 'none',
                                fontWeight: isActive || item.level === 2 ? 700 : 500,
                                fontSize: item.level === 2 ? '0.96rem' : '0.9rem',
                                pl: item.level === 2 ? 1.2 : 2.2,
                                pr: 1,
                                py: 0.8,
                                ml: item.level === 2 ? -1.2 : -0.8,
                                borderLeft: '2px solid',
                                borderLeftColor: isActive ? '#016f57' : item.level === 2 ? 'rgba(13, 28, 79, 0.1)' : 'transparent',
                                borderRadius: '0 10px 10px 0',
                                lineHeight: 1.45,
                                bgcolor: isActive ? 'rgba(1, 111, 87, 0.08)' : 'transparent',
                                transition: 'all 0.18s ease',
                                '&:hover': {
                                  color: '#016f57',
                                  bgcolor: 'rgba(1, 111, 87, 0.06)',
                                  borderLeftColor: 'rgba(1, 111, 87, 0.35)',
                                },
                              }}
                            >
                              {item.text}
                            </Box>
                          );
                        })()
                      ))}
                    </Stack>
                  </Box>
                </Paper>
              </Box>
            )}
          </Box>
        </Container>
      </Box>

      <Box className="legal-print-hide">
        <ScrollToTopButton threshold={500} ariaLabel="Back to top" />
        <Box ref={footerRef}>
          <Footer />
        </Box>
      </Box>
    </Box>
  );
}
